import React from 'react';
import Image from 'gatsby-plugin-sanity-image';
import classnames from 'classnames';
import { getImageDimensions, getFileAsset } from '@sanity/asset-utils';
import { PortableText } from '@portabletext/react';
import slugify from 'slugify';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { TwitterTweetEmbed } from 'react-twitter-embed';

import List from './List';
import Button from './Button';
import GitHubStar from './GitHubStar';
import CustomerQuote from './CustomerQuote';
import Collapsible from './Collapsible';
import Logos from './pancakes/Logos';

import LinkIcon from '../icons/Link';

import './SanityPortableText.scss';

const baseImageComponent = ({ value }) => {
  const { image, altText, float, caption } = value;
  const { width, height, aspectRatio } = getImageDimensions(image);

  const imageSize = {
    w: aspectRatio > 1 && width > 952 ? 952 : width,
    h:
      aspectRatio > 1 && width > 952
        ? Math.floor((952 * height) / width)
        : height,
  };

  const floatClass = float ? `baseImageFloat${float}` : '';

  return (
    <figure className="baseFigure">
      <Image
        {...image}
        width={imageSize.w}
        height={imageSize.h}
        sizes={`(max-width: 1000px) 100vw, ${imageSize.w}px`}
        alt={altText || ''}
        className={classnames('baseImage', floatClass)}
        style={{ width: aspectRatio < 1 && imageSize.w / 2 }}
      />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

const listCheckComponent = ({ children, value }) => {
  return <List icon={value.listItem}>{children}</List>;
};

const listItemComponent = ({ children, value }) => {
  return <List.ListItem icon={value.listItem}>{children}</List.ListItem>;
};

const videoFileComponent = ({ value }) => {
  const video = getFileAsset(value.asset, {
    projectId: process.env.GATSBY_SANITY_PROJECTID,
    dataset: process.env.GATSBY_SANITY_DATASET,
  });

  return (
    <video controls className="PortableTextVideoPlayer">
      <source src={video.url} type={`video/${video.extension}`} />
    </video>
  );
};

const youtubeVideoComponent = ({ value }) => {
  return (
    <iframe
      width="560"
      height="315"
      src={`https://www.youtube.com/embed/${value.videoID}`}
      title="YouTube video player"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
};

const baseButtonComponent = ({ value }) => {
  let theme;
  if (value.theme === 'Green') theme = 'corporate-light';
  if (value.theme === 'Transparent')
    theme = 'corporate-light transparent no-padding';
  if (value.theme === 'TransparentTeal')
    theme = 'corporate-light transparent teal no-padding';

  const { arrow, label, url } = value;

  return (
    <div className="PortableTextButton">
      <Button className={theme} arrow={arrow} to={url}>
        {label}
      </Button>
    </div>
  );
};

const basePeopleComponent = ({ value, children }) => {
  const { reference, linkToTwitter } = value;

  const People = () => {
    return (
      <>
        <Image {...value.reference.image} width={100} height={100} alt="" />
        {children}
      </>
    );
  };

  if (linkToTwitter && reference.twitter) {
    return (
      <a
        className="basePeopleComponent"
        href={`https://www.twitter.com/${reference.twitter}`}
      >
        <People />
      </a>
    );
  } else {
    return (
      <span className="basePeopleComponent">
        <People />
      </span>
    );
  }
};

const githubStarsComponent = ({ value }) => {
  return <GitHubStar repo={value.repo} />;
};

const customerQuoteComponent = ({ value }) => {
  return (
    <div className="CustomerQuoteBlock">
      <CustomerQuote
        author={value.author}
        hideThumbnail={!value.showAuthorsPicture}
      >
        {value.quote}
      </CustomerQuote>
    </div>
  );
};

const callToActionComponent = ({ value }) => {
  return (
    <section className="CallToActionComponent">
      <p className="CallToActionComponentTitle">{value.title}</p>
      <div className="CallToActionComponentButtons">
        {value?.callToActions?.callToActions.map((cta, index) => (
          <Button
            className={classnames(
              'with-shadow',
              index === 0 ? 'corporate-light' : 'light with-border'
            )}
            to={cta.url}
            external
            key={index}
          >
            {cta.label}
          </Button>
        ))}
      </div>
    </section>
  );
};

const codeBlockComponent = ({ value }) => {
  return (
    <div className="gatsby-highlight">
      <SyntaxHighlighter
        language={value.language}
        useInlineStyles={false}
        codeTagProps={{}}
      >
        {value.code}
      </SyntaxHighlighter>
    </div>
  );
};

const headingWithAnchorComponent = (as, text) => {
  const slug =
    typeof text[0] === 'string'
      ? slugify(text[0], { lower: true, strict: true })
      : null;
  const Component = as;

  return (
    <Component id={slug}>
      {slug && (
        <a
          href={`#${slug}`}
          aria-label={`${text} permalink`}
          className="HeadingAnchorLink"
        >
          <LinkIcon />
        </a>
      )}
      {text}
    </Component>
  );
};

const twitterEmbedComponent = ({ value }) => {
  return (
    <div className="TwitterTweetEmbedContainer">
      <TwitterTweetEmbed tweetId={value.tweetID} />
    </div>
  );
};

const arcadeEmbedComponent = ({ value }) => {
  return (
    <div
      style={{
        position: 'relative',
        paddingBottom: 'calc(66.66666666666666% + 41px)',
        height: 0,
      }}
    >
      <iframe
        src={`https://demo.arcade.software/${value.arcadeID}?embed`}
        frameborder="0"
        loading="lazy"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          colorScheme: 'light',
        }}
        title="Arcade Demo"
      ></iframe>
    </div>
  );
};

const navatticEmbedComponent = ({ value }) => {
  return (
    <>
      <iframe
        src={`https://capture.navattic.com/${value.navatticID}`}
        className="NavatticIframe"
        allow="fullscreen"
      ></iframe>
      <Button
        to={`https://capture.navattic.com/${value.navatticID}`}
        target="_blank"
        rel="noreferrer noopener"
        className="NavatticMobileButton corporate-light"
        external
      >
        {value.mobileLabel}
      </Button>
    </>
  );
};

const collapsibleComponent = ({ value }) => {
  return (
    <Collapsible title={value.title}>
      <SanityPortableText blocks={value.text} />
    </Collapsible>
  );
};

const highlightComponent = ({ value }) => {
  return (
    <div className="HighlightSection">
      <SanityPortableText blocks={value.text} />
    </div>
  );
};

const tableComponent = ({ value }) => {
  const { rows } = value;

  return (
    <table className="Table">
      {rows.map((row, index) => {
        if (index === 0) {
          return (
            <thead>
              <tr>
                {row.cells.map((c) => (
                  <td>{c}</td>
                ))}
              </tr>
            </thead>
          );
        }

        if (index > 0) {
          return (
            <tbody>
              <tr>
                {row.cells.map((c) => (
                  <td>{c}</td>
                ))}
              </tr>
            </tbody>
          );
        }
      })}
    </table>
  );
};

const logosComponent = ({ value }) => {
  return (
    <Logos
      title={value.pancakeIntro?.pancakeTitle}
      text={value.pancakeIntro?.pancakeText}
      logos={value.logos}
      theme={value.theme}
      showCompanyName={value.showCompanyName}
      roundedLogos={value.roundedLogos}
      useLogomarkVersion={value.useLogomarkVersion}
      wrapLogosInCards={value.wrapLogosInCards}
      key={value._key}
    />
  );
};

const SanityPortableText = (props) => {
  const customComponents = {
    types: {
      baseImage: baseImageComponent,
      videoFile: videoFileComponent,
      youtubeVideo: youtubeVideoComponent,
      baseButton: baseButtonComponent,
      githubStars: githubStarsComponent,
      customerQuote: customerQuoteComponent,
      callToActionPancake: callToActionComponent,
      code: codeBlockComponent,
      twitterEmbed: twitterEmbedComponent,
      collapsible: collapsibleComponent,
      table: tableComponent,
      arcade: arcadeEmbedComponent,
      navattic: navatticEmbedComponent,
      highlight: highlightComponent,
      logos: logosComponent,
    },
    list: {
      check: listCheckComponent,
    },
    listItem: {
      check: listItemComponent,
    },
    marks: {
      basePeople: basePeopleComponent,
    },
    block: {
      h1: ({ children }) => headingWithAnchorComponent('h1', children),
      h2: ({ children }) => headingWithAnchorComponent('h2', children),
      h3: ({ children }) => headingWithAnchorComponent('h3', children),
      h4: ({ children }) => headingWithAnchorComponent('h4', children),
      h5: ({ children }) => headingWithAnchorComponent('h5', children),
      h6: ({ children }) => headingWithAnchorComponent('h6', children),
    },
  };

  return <PortableText value={props.blocks} components={customComponents} />;
};

export default SanityPortableText;
